'use client';

/**
 * Cart Context
 * Provides full cart state (items, subtotal, counts) for header display and mini cart
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface CartProductImage {
  id: number;
  url: string;
  altText: string | null;
  position: number;
  role: string | null;
}

export interface CartProductSummary {
  id: number;
  sku: string;
  name: string;
  slug: string;
  basePrice: string;
  images: CartProductImage[];
}

export interface CartSelectedOption {
  optionId: number;
  valueId: number;
  title?: string;
  valueTitle?: string;
  priceModifier?: string;
}

export interface CartItem {
  id: number;
  productId: number;
  quantity: number;
  calculatedPrice: string;
  lineTotal: string;
  selectedOptions: CartSelectedOption[];
  product: CartProductSummary;
}

interface CartContextValue {
  items: CartItem[];
  itemCount: number;
  subtotal: string;
  isLoading: boolean;
  isAddingToCart: boolean;
  refreshCart: () => Promise<void>;
}

// ============================================================================
// Context
// ============================================================================

const CartContext = createContext<CartContextValue | null>(null);

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

interface CartProviderProps {
  children: ReactNode;
}

export function CartProvider({ children }: CartProviderProps) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [itemCount, setItemCount] = useState(0);
  const [subtotal, setSubtotal] = useState('0.00');
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [mounted, setMounted] = useState(false);

  const refreshCart = useCallback(async () => {
    if (typeof window === 'undefined') return;

    try {
      const sessionId = localStorage.getItem('cart_session_id');

      if (!sessionId) {
        setItems([]);
        setItemCount(0);
        setSubtotal('0.00');
        return;
      }

      const response = await fetch(`/api/cart?sessionId=${sessionId}`);

      if (!response.ok) {
        setItems([]);
        setItemCount(0);
        setSubtotal('0.00');
        return;
      }

      const { data } = await response.json();
      setItems(data?.items ?? []);
      setItemCount(data?.itemCount ?? 0);
      setSubtotal(data?.subtotal ?? '0.00');
    } catch {
      setItems([]);
      setItemCount(0);
      setSubtotal('0.00');
    } finally {
      setIsLoading(false);
      setIsAddingToCart(false);
    }
  }, []);

  // Set mounted state
  useEffect(() => {
    setMounted(true);
  }, []);

  // Fetch cart on mount
  useEffect(() => {
    if (mounted) {
      refreshCart();
    }
  }, [mounted, refreshCart]);

  // Listen for cart-adding event (fired before add-to-cart fetch)
  useEffect(() => {
    const handleCartAdding = () => {
      setIsAddingToCart(true);
    };

    window.addEventListener('cart-adding', handleCartAdding);
    return () => window.removeEventListener('cart-adding', handleCartAdding);
  }, []);

  // Listen for cart-updated event (fired after add-to-cart fetch succeeds)
  useEffect(() => {
    const handleCartUpdate = () => {
      refreshCart();
    };

    window.addEventListener('cart-updated', handleCartUpdate);
    return () => window.removeEventListener('cart-updated', handleCartUpdate);
  }, [refreshCart]);

  return (
    <CartContext.Provider value={{ items, itemCount, subtotal, isLoading, isAddingToCart, refreshCart }}>
      {children}
    </CartContext.Provider>
  );
}
